/** @typedef {import('../index.js').InvalidDocData} InvalidDocData */
/** @typedef {import('./upload.js').UploadDocData} UploadDocData */
/** @typedef {import('./flowCell.js').FlowCellDocData} FlowCellDocData */

/**
 * we name this folder 'parts' to match the Firestore collection name 'parts'
 * but this collection is really about customer "Uploaded Parts"
 */
import Upload from './upload.js';
import FlowCell from './flowCell.js';

/**
 * Converts a Firestore document snapshot into an {@link Upload} / {@link FlowCell} instance.
 * @param {any} doc Raw Firestore document snapshot.
 * @returns {Upload | FlowCell | InvalidDocData} An {@link Upload} or {@link FlowCell} instance. Returns {@link InvalidDocData} if transformation fails.
 */
function uploadPartFromFirestore(doc) {
  const docData = { id: doc.id, ...doc.data() };
  try {
    if (docData.isConfigurable && docData.configurableParameters?.type === 'flowCell') {
      return FlowCell.fromFirestore(docData);
    }
    return Upload.fromFirestore(docData);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error transforming part data:', docData.id, error);
    /** @type {InvalidDocData} */
    const InvalidDocData = { validation: 'failed', ...docData };
    return InvalidDocData;
  }
}

/**
 * Converts an {@link Upload} or {@link FlowCell} instance into a Firestore-compatible object.
 * @param {Upload | FlowCell} part A part instance.
 * @returns {Omit<UploadDocData, 'id'> | Omit<FlowCellDocData, 'id'>} Firestore data (without `id`).
 */
function uploadPartToFirestore(part) {
  if (part.isConfigurable && part.configurableParameters?.type === 'flowCell') {
    return new FlowCell(part).toFirestore();
  }
  return new Upload(part).toFirestore();
}

export {
  Upload, // exported for TS type inference
  FlowCell, // exported for TS type inference
  uploadPartFromFirestore,
  uploadPartToFirestore,
};
